/**
 * @module features/clients/components/ClientEditModal
 * @description Modal for editing organization profile, address, contact person,
 * theme color, and seat quota limits (with capacity shrink validation).
 */
import * as React from "react";
import { Edit2, AlertCircle, Building2, Upload, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ClientDetail, ClientSummary } from "../types";
import { ColorPickerInput } from "./ColorPickerInput";
import { useUpdateClient, useUploadClientLogo, useDeleteClientLogo } from "../api/clientQueries";

export interface ClientEditModalProps {
  client: ClientDetail | ClientSummary | null;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function ClientEditModal({
  client,
  open,
  onClose,
  onSuccess,
}: ClientEditModalProps) {
  const [activeTab, setActiveTab] = React.useState<"identity" | "address" | "contact" | "quota">("identity");
  const [orgName, setOrgName] = React.useState("");
  const [orgContactNo, setOrgContactNo] = React.useState("");
  const [themeColor, setThemeColor] = React.useState("#2563EB");
  const [street, setStreet] = React.useState("");
  const [landmark, setLandmark] = React.useState("");
  const [city, setCity] = React.useState("");
  const [state, setState] = React.useState("");
  const [country, setCountry] = React.useState("India");
  const [pincode, setPincode] = React.useState("");
  const [contactName, setContactName] = React.useState("");
  const [contactEmail, setContactEmail] = React.useState("");
  const [contactMobile, setContactMobile] = React.useState("");
  const [maxUserLimit, setMaxUserLimit] = React.useState(10);
  const [notificationEmails, setNotificationEmails] = React.useState("");
  const [notificationMobiles, setNotificationMobiles] = React.useState("");
  const [logoFile, setLogoFile] = React.useState<File | null>(null);
  const [logoPreview, setLogoPreview] = React.useState<string | null>(null);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const updateMutation = useUpdateClient();
  const uploadLogoMutation = useUploadClientLogo();
  const deleteLogoMutation = useDeleteClientLogo();

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (client && open) {
      setOrgName(client.org_name || "");
      setOrgContactNo(client.org_contact_no || "");
      setThemeColor(client.theme_color || "#2563EB");
      setStreet((client as any).street || "");
      setLandmark((client as any).landmark || "");
      setCity(client.city || "");
      setState(client.state || "");
      setCountry((client as any).country || "India");
      setPincode((client as any).pincode || "");
      setContactName(client.contact_person_name || "");
      setContactEmail((client as any).contact_person_email || "");
      setContactMobile((client as any).contact_person_mobile_no || "");
      setMaxUserLimit(client.max_user_limit || 10);
      setNotificationEmails(
        ((client as any).notification_email_ids || []).join(", ")
      );
      setNotificationMobiles(
        ((client as any).notification_mobile_nos || []).join(", ")
      );
      setLogoPreview(client.logo_url || null);
      setLogoFile(null);
      setErrorMessage(null);
      setActiveTab("identity");
    }
  }, [client, open]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setErrorMessage("Logo file size must not exceed 2MB.");
      return;
    }
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = () => setLogoPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleDeleteLogo = async () => {
    if (!client) return;
    try {
      await deleteLogoMutation.mutateAsync(client.id);
      setLogoPreview(null);
      setLogoFile(null);
    } catch (err: any) {
      setErrorMessage("Failed to remove logo.");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!client) return;

    // Validate quota shrink guard
    if (maxUserLimit < client.active_user_count) {
      setErrorMessage(
        `Cannot reduce user limit to ${maxUserLimit}. There are already ${client.active_user_count} active linked users.`
      );
      return;
    }

    setErrorMessage(null);

    try {
      // 1. Upload logo if changed
      if (logoFile) {
        await uploadLogoMutation.mutateAsync({
          clientId: client.id,
          file: logoFile,
        });
      }

      // 2. Update client details
      await updateMutation.mutateAsync({
        clientId: client.id,
        data: {
          org_name: orgName,
          org_contact_no: orgContactNo,
          theme_color: themeColor,
          street: street || undefined,
          landmark: landmark || undefined,
          city: city || undefined,
          state: state || undefined,
          country: country || undefined,
          pincode: pincode || undefined,
          contact_person_name: contactName || undefined,
          contact_person_email: contactEmail || undefined,
          contact_person_mobile_no: contactMobile || undefined,
          max_user_limit: maxUserLimit,
          notification_email_ids_raw: notificationEmails,
          notification_mobile_nos_raw: notificationMobiles,
        },
      });

      onSuccess?.();
      onClose();
    } catch (err: any) {
      setErrorMessage(
        err?.response?.data?.error_message ||
          err?.message ||
          "Failed to update client details."
      );
    }
  };

  const isSubmitting =
    updateMutation.isPending ||
    uploadLogoMutation.isPending ||
    deleteLogoMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] flex flex-col p-0">
        <DialogHeader className="p-5 pb-3 border-b border-zinc-100 dark:border-zinc-800">
          <DialogTitle className="text-base font-semibold flex items-center gap-2">
            <Edit2 className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            Edit Organization: {client?.org_name}
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground mt-1">
            Update profile, contact, physical address, theme styling, and seat capacity.
          </DialogDescription>
        </DialogHeader>

        {/* Tab navigation */}
        <div className="flex border-b border-zinc-100 dark:border-zinc-800 px-5 gap-4 text-xs font-medium">
          <button
            type="button"
            onClick={() => setActiveTab("identity")}
            className={`py-2.5 border-b-2 transition-colors ${
              activeTab === "identity"
                ? "border-zinc-900 dark:border-white text-foreground font-semibold"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            Identity & Theme
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("address")}
            className={`py-2.5 border-b-2 transition-colors ${
              activeTab === "address"
                ? "border-zinc-900 dark:border-white text-foreground font-semibold"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            Address
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("contact")}
            className={`py-2.5 border-b-2 transition-colors ${
              activeTab === "contact"
                ? "border-zinc-900 dark:border-white text-foreground font-semibold"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            Contact Person
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("quota")}
            className={`py-2.5 border-b-2 transition-colors ${
              activeTab === "quota"
                ? "border-zinc-900 dark:border-white text-foreground font-semibold"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            Quotas & Alerts
          </button>
        </div>

        {errorMessage && (
          <div className="mx-5 mt-3 p-3 rounded-lg bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 text-xs text-red-600 dark:text-red-400 flex items-center gap-2">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}

        {/* Form Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {activeTab === "identity" && (
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-foreground">Organization Name *</label>
                <Input
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  className="mt-1 h-9 text-xs"
                  required
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-foreground">Organization Contact Number *</label>
                <Input
                  value={orgContactNo}
                  onChange={(e) => setOrgContactNo(e.target.value)}
                  className="mt-1 h-9 text-xs"
                  required
                />
              </div>

              {/* Logo Management */}
              <div>
                <label className="text-xs font-semibold text-foreground">Organization Logo</label>
                <div className="flex items-center gap-4 mt-2">
                  {logoPreview ? (
                    <div className="relative group">
                      <img
                        src={logoPreview}
                        alt="Logo preview"
                        className="h-14 w-14 rounded-xl object-contain border border-zinc-200 dark:border-zinc-800 bg-white p-1"
                      />
                      <button
                        type="button"
                        onClick={handleDeleteLogo}
                        className="absolute -top-1.5 -right-1.5 p-1 rounded-full bg-red-600 text-white shadow-xs hover:bg-red-700 transition-colors"
                        title="Remove Logo"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="h-14 w-14 rounded-xl border border-dashed border-zinc-300 dark:border-zinc-700 flex items-center justify-center text-zinc-400">
                      <Building2 className="h-6 w-6" />
                    </div>
                  )}

                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/svg+xml"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      className="text-xs gap-1.5 h-8"
                    >
                      <Upload className="h-3.5 w-3.5" />
                      {logoPreview ? "Replace Logo" : "Upload Logo"}
                    </Button>
                    <p className="text-[11px] text-muted-foreground mt-1">
                      PNG, JPEG, WebP, or SVG up to 2MB.
                    </p>
                  </div>
                </div>
              </div>

              {/* Theme Color Picker */}
              <div>
                <label className="text-xs font-semibold text-foreground">Portal Theme Color *</label>
                <p className="text-[11px] text-muted-foreground mb-2">
                  This primary brand color dynamically styles the Client Portal interface for this organization's users.
                </p>
                <ColorPickerInput value={themeColor} onChange={setThemeColor} />
              </div>
            </div>
          )}

          {activeTab === "address" && (
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-foreground">Street Address *</label>
                <Input
                  value={street}
                  onChange={(e) => setStreet(e.target.value)}
                  className="mt-1 h-9 text-xs"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-foreground">Landmark (Optional)</label>
                <Input
                  value={landmark}
                  onChange={(e) => setLandmark(e.target.value)}
                  className="mt-1 h-9 text-xs"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-foreground">City *</label>
                  <Input
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="mt-1 h-9 text-xs"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-foreground">State / Province *</label>
                  <Input
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    className="mt-1 h-9 text-xs"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-foreground">Country *</label>
                  <Input
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className="mt-1 h-9 text-xs"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-foreground">Pincode *</label>
                  <Input
                    value={pincode}
                    onChange={(e) => setPincode(e.target.value)}
                    className="mt-1 h-9 text-xs"
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === "contact" && (
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-foreground">Contact Person Full Name *</label>
                <Input
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  className="mt-1 h-9 text-xs"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-foreground">Official Email *</label>
                <Input
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  className="mt-1 h-9 text-xs"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-foreground">Direct Mobile Number *</label>
                <Input
                  value={contactMobile}
                  onChange={(e) => setContactMobile(e.target.value)}
                  className="mt-1 h-9 text-xs"
                />
              </div>
            </div>
          )}

          {activeTab === "quota" && (
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-foreground">Maximum User Seat Limit *</label>
                <Input
                  type="number"
                  min={client?.active_user_count || 1}
                  value={maxUserLimit}
                  onChange={(e) => setMaxUserLimit(parseInt(e.target.value, 10) || 1)}
                  className="mt-1 h-9 text-xs"
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Currently {client?.active_user_count || 0} active linked user(s). The limit cannot be lower than the active count.
                </p>
              </div>

              <div>
                <label className="text-xs font-semibold text-foreground">Notification Email IDs</label>
                <Input
                  value={notificationEmails}
                  onChange={(e) => setNotificationEmails(e.target.value)}
                  placeholder="alerts@client.com, ops@client.com"
                  className="mt-1 h-9 text-xs"
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Comma-separated email addresses to receive platform operational alerts.
                </p>
              </div>

              <div>
                <label className="text-xs font-semibold text-foreground">Notification Mobile Numbers</label>
                <Input
                  value={notificationMobiles}
                  onChange={(e) => setNotificationMobiles(e.target.value)}
                  placeholder="+91-9876543210, +91-9123456789"
                  className="mt-1 h-9 text-xs"
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Comma-separated phone numbers for SMS / WhatsApp alerts.
                </p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="p-4 border-t border-zinc-100 dark:border-zinc-800 gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? "Saving Changes..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
